import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../firebase';
import {
  getPrimaryContactMethod,
  normalizeContactMethods,
} from '../utils/contactMethods';

const getCallableErrorMessage = (error, fallback) => {
  const backendMessage =
    typeof error?.message === 'string'
      ? error.message.replace(/^FirebaseError:\s*/i, '').trim()
      : '';

  if (backendMessage) {
    return backendMessage;
  }

  if (error?.code === 'functions/unauthenticated') {
    return 'You must be signed in to perform this action.';
  }

  if (error?.code === 'functions/permission-denied') {
    return 'You are not authorized to perform this action.';
  }

  return fallback;
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeSupplierContact = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};
  const contactMethods = normalizeContactMethods(data, {
    numberKeys: ['contactNumber', 'phone'],
    whatsappKeys: ['contactWhatsapp', 'whatsapp'],
    defaultLabel: 'Mobile',
  });
  const primaryContactMethod = getPrimaryContactMethod(contactMethods);

  return {
    id: docSnapshot?.id || data.id,
    supplierId: normalizeString(data?.supplierId),
    name: normalizeString(data?.name),
    role: normalizeString(data?.role),
    contactMethods,
    phone: primaryContactMethod?.number || '',
    whatsapp: primaryContactMethod?.hasWhatsapp ? primaryContactMethod.number : '',
    email: normalizeString(data?.email),
    isPrimary: data?.isPrimary === true,
    createdAt: data?.createdAt ?? null,
  };
};

const buildContactPayload = (contact = {}) => ({
  name: normalizeString(contact.name),
  role: normalizeString(contact.role),
  contactMethods: normalizeContactMethods(contact, {
    numberKeys: ['contactNumber', 'phone'],
    whatsappKeys: ['contactWhatsapp', 'whatsapp'],
    defaultLabel: 'Mobile',
  }),
  email: normalizeString(contact.email).toLowerCase(),
});

export const subscribeToSupplierContacts = (supplierId, onNext, onError) => {
  const normalizedSupplierId = normalizeString(supplierId);

  if (!normalizedSupplierId) {
    onNext([]);
    return () => {};
  }

  const contactsQuery = query(
    collection(db, 'supplierContacts'),
    where('supplierId', '==', normalizedSupplierId),
    orderBy('isPrimary', 'desc')
  );

  return onSnapshot(
    contactsQuery,
    (querySnapshot) => {
      const contacts = querySnapshot.docs.map((docSnapshot) =>
        normalizeSupplierContact(docSnapshot)
      );

      onNext(contacts);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  );
};

export const createSupplierContact = async (supplierId, contact) => {
  try {
    const createContact = httpsCallable(functions, 'createSupplierContact');
    const result = await createContact({
      supplierId: normalizeString(supplierId),
      ...buildContactPayload(contact),
    });

    return result.data;
  } catch (error) {
    throw new Error(getCallableErrorMessage(error, 'Failed to create contact.'));
  }
};

export const updateSupplierContact = async (contactId, contact) => {
  try {
    const updateContact = httpsCallable(functions, 'updateSupplierContact');
    const result = await updateContact({
      contactId: normalizeString(contactId),
      ...buildContactPayload(contact),
    });

    return result.data;
  } catch (error) {
    throw new Error(getCallableErrorMessage(error, 'Failed to update contact.'));
  }
};

export const deleteSupplierContact = async (contactId) => {
  try {
    const deleteContact = httpsCallable(functions, 'deleteSupplierContact');
    const result = await deleteContact({ contactId: normalizeString(contactId) });

    return result.data;
  } catch (error) {
    throw new Error(getCallableErrorMessage(error, 'Failed to delete contact.'));
  }
};

export const setPrimarySupplierContact = async (supplierId, contactId) => {
  try {
    const setPrimaryContact = httpsCallable(functions, 'setPrimarySupplierContact');
    const result = await setPrimaryContact({
      supplierId: normalizeString(supplierId),
      contactId: normalizeString(contactId),
    });

    return result.data;
  } catch (error) {
    throw new Error(getCallableErrorMessage(error, 'Failed to set primary contact.'));
  }
};
